import { useCallback, useState } from "react";

import { Box, Flex } from "@chakra-ui/react";
import { useNavigate } from "react-router";
import { useShallow } from "zustand/react/shallow";

import { RestockingItemStatus } from "__graphql__/types";
import { EppoFeatureFlags } from "app/types/flags";
import { routes } from "config/routes";
import { EndDroppingProcessButton } from "flows/Inbound/components/EndDroppingProcessButton";
import { usePrivateRestockingList } from "flows/Inventory/flows/RestockingList/services/restockingService/service";
import { ScanPlaceholder } from "flows/Inventory/shared/components/ScanPlaceholder";
import { useAnalytics } from "shared/hooks/useAnalytics";
import { useEppoFeatureFlagProvider } from "shared/hooks/useEppoFeatureFlag";
import { useListScrollEffects } from "shared/hooks/useListScrollEffects";
import { BOTTOM_TAB_BAR_HEIGHT_NUMBER } from "ui/BottomTabBarContainer";
import { Spinner } from "ui/Spinner/Spinner";

import { NewRestockingListItem, RestockingListItem } from "./RestockingListItem";
import { useRestockingServiceStore } from "../stores/restockingServiceStore";

export function RestockingListPreparationList({
  isLoading,
  scrollContainer,
}: {
  isLoading: boolean;
  scrollContainer: React.RefObject<HTMLDivElement | null>;
}) {
  const navigate = useNavigate();
  const { sendSegmentTrackEvent } = useAnalytics();
  const { privateRestockingListData, filteredPreparationItems, removeRestockingItem } =
    usePrivateRestockingList();

  const { isFeatureEnabled: isRestockingV2Enabled } = useEppoFeatureFlagProvider(
    EppoFeatureFlags.ENABLE_RESTOCKING_V2,
  );

  const [itemBeingRemoved, setItemBeingRemoved] = useState<string | null>(null);

  const { activeItemId, setUseRestockingServiceStore } = useRestockingServiceStore(
    useShallow((state) => ({
      activeItemId: state.activeItemId,
      setUseRestockingServiceStore: state.setUseRestockingServiceStore,
    })),
  );

  const { itemsRef, scrollContainerRef, setScrollPosition } = useListScrollEffects({
    activeItemId: activeItemId ?? null,
    listItemIds: filteredPreparationItems?.map((item) => item.sku) ?? [],
    scrollOffset: 70,
  });

  const handleRemoveRestockingItem = useCallback(
    async (sku: string) => {
      if (!scrollContainerRef.current) return;
      const scrollPosition = scrollContainerRef.current.scrollTop;
      try {
        setItemBeingRemoved(sku);
        await removeRestockingItem(sku);
        if (activeItemId === sku) {
          setUseRestockingServiceStore({ activeItemId: null });
        }
      } finally {
        setItemBeingRemoved(null);
        setTimeout(() => {
          setScrollPosition(scrollPosition);
        }, 0);
      }
    },
    [
      activeItemId,
      removeRestockingItem,
      scrollContainerRef,
      setScrollPosition,
      setUseRestockingServiceStore,
    ],
  );

  const startRestockingList = () => {
    setUseRestockingServiceStore({ activeItemId: null });
    sendSegmentTrackEvent("restockingStateUpdated", {
      restocking_list_id: privateRestockingListData?.id ?? "",
      state: "restocking_list_started",
    });
    navigate(routes.inventory.restockingList.dropOff);
  };

  if (isLoading) {
    return (
      <Flex h="100%" alignItems="center" justifyContent="center">
        <Spinner />
      </Flex>
    );
  }
  if (!filteredPreparationItems?.length) {
    return (
      <ScanPlaceholder
        title="pages.inventory.restocking-list.instructions.title"
        subtitle="pages.inventory.restocking-list.instructions.subtitle"
        justifyContent="center"
        flexGrow="1"
        px="s400"
      />
    );
  }
  return (
    <Box w="100%" h="100%" ref={scrollContainerRef}>
      <Flex
        flex={1}
        flexDirection="column"
        alignItems="center"
        justifyContent="flex-start"
        pt="s100"
        pb="72px"
        gap="s200"
      >
        {isRestockingV2Enabled
          ? filteredPreparationItems.map((restockingItem, index) => (
              <NewRestockingListItem
                key={restockingItem.sku}
                item={restockingItem}
                onRemove={handleRemoveRestockingItem}
                scrollContainer={scrollContainer}
                elementRef={itemsRef.current[index]}
              />
            ))
          : filteredPreparationItems.map((restockingItem, index) => (
              <RestockingListItem
                key={restockingItem.sku}
                restockingItem={restockingItem}
                onClickRemove={handleRemoveRestockingItem}
                isShowingSwipeButtons={restockingItem.status !== RestockingItemStatus.done}
                isShowingCompleteCardsAsBlocked
                scrollContainer={scrollContainer}
                isDisabled={itemBeingRemoved === restockingItem.sku}
                elementRef={itemsRef.current[index]}
              />
            ))}
        <EndDroppingProcessButton
          shouldShowButton
          onClickButton={startRestockingList}
          labelMessageId="flows.inventory.restocking-list.pages.list-preparation.start-restocking"
          isLoading={false}
          bottom={BOTTOM_TAB_BAR_HEIGHT_NUMBER}
        />
      </Flex>
    </Box>
  );
}
